import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { Task_Entity } from "../../entities/task";
import { filmsApi } from "../api/filmsApi";
import { scheduleInvalidation } from "../api/deferredInvalidation";

const tasksAdapter = createEntityAdapter<Task_Entity>();
const initialState = tasksAdapter.getInitialState();

/** Everything a task edit feeds into, refreshed once entry settles. */
const TASK_EDIT_TAGS = [
  "tasks",
  "projectTasks",
  "jobs",
  "budgets",
  "projects",
  "vtransactions",
];

export const taskEndpoints = filmsApi.injectEndpoints({
  endpoints: (build) => ({
    getTasks: build.query<EntityState<Task_Entity>, void>({
      query: () => `tasks`,
      transformResponse: (responseData: Task_Entity[]) => {
        return tasksAdapter.setAll(initialState, responseData);
      },
      providesTags: ["tasks"],
    }),
    getTask: build.query<Task_Entity, number>({
      query: (id) => `tasks/${id}`,
      providesTags: (result, error, id) => [{ type: "tasks", id }],
    }),
    createTask: build.mutation<Task_Entity, Partial<Task_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `tasks`,
      }),
      // Match update/delete/batch: also refresh the contract panel
      // (projectTasks) and jobs so they don't lag behind the Budget "Actual".
      invalidatesTags: [
        "tasks",
        "projectTasks",
        "jobs",
        "budgets",
        "projects",
        "vtransactions",
      ],
    }),
    updateTask: build.mutation<
      void,
      { id: number; body: Partial<Task_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `tasks/${args.id}`,
      }),
      // Deliberately no invalidatesTags: a task edit invalidates the query
      // feeding its own grid, so refreshing per cell rebuilt the rows mid-typing
      // and dropped everything typed after the first value. See
      // scheduleInvalidation.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The grid reverts the cell and reports it; still refresh so the row
          // settles on what the server actually holds.
        }
        scheduleInvalidation(dispatch, TASK_EDIT_TAGS);
      },
    }),
    updateBatchTasks: build.mutation<void, Partial<Task_Entity>[]>({
      query: (args) => ({
        method: "PUT",
        body: args,
        url: `tasks`,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          /* as above */
        }
        scheduleInvalidation(dispatch, TASK_EDIT_TAGS);
      },
    }),
    deleteTask: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `tasks/${id}`,
      }),
      invalidatesTags: [
        "tasks",
        "projectTasks",
        "jobs",
        "budgets",
        "projects",
        "vtransactions",
      ],
    }),
  }),
});

export const {
  useGetTaskQuery,
  useGetTasksQuery,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
  useUpdateBatchTasksMutation,
} = taskEndpoints;

export default taskEndpoints;
